import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Prompt, useParams } from "react-router-dom";

import ProjectAPI from "@/src/api/ProjectAPI";

import CastingPageEditor from "./components/CastingPageEditor";
import CommonTypeEditor from "./components/CommonTypeEditor";
import OffersPageEditor from "./components/OffersPageEditor";
import SearchPageEditor from "./components/SearchPageEditor";
import SmartlistPageEditor from "./components/SmartlistPageEditor";
import {
  CREATE_COMPONENT,
  UPDATE_COMPONENT,
  UPDATE_PROJECT,
} from "../../actions/ActionTypes";
import { setProject } from "../../actions/architecture";
import { notifError, notifSuccess } from "../../actions/FlashMessageActions";
import { postApi, putApi } from "../../actions/SendActions";
import ContentPageEditor from "./components/content/ContentPageEditor";
import SignupPageEditor from "./components/signup/SignupPageEditor";
import { uploadWidget } from "../../api/legacy/ComponentAPI";
import history from "../../components/common/navigation/AppHistory";
import { PROJECTS_ROUTE } from "../../components/routes/routes";
import Breadcrumbs from "../../components/utils/Breadcrumbs";
import ContainerHeader from "../../components/utils/ContainerHeader";
import { getPageLinkTranslation } from "../../components/utils/Translations";
import { COMPONENT_TYPES } from "../../constants/componentType";
import {
  ADDITIONNAL_IMAGE_FORMATS,
  DEFAULT_IMAGE_FORMATS,
} from "../../constants/ratio";
import "../../css/admin/admin.scss";
import "../../css/admin/platform.scss";
import {
  getComponentByType,
  getComponentByTypeOrName,
} from "../../utils/data/Architecture";

import type { JSX } from "react";

import type {
  ContentPagePlatformComponent,
  PageLink,
  Platform,
  PlatformComponent,
  Project,
  ProjectTranslation,
  SearchPagePlatformComponent,
  Section,
  StorefrontProjectParams,
  WidgetComponent,
} from "../../api/ProjectAPI.types";
import type { AppState } from "../../reducers";

const WIDGET_URL_TYPE = "url";
const WIDGET_HTML_TYPE = "html_template";

export const SECTIONS_WITH_SAVED_COMPONENTS: Section[] = [
  "content_page",
  "casting_page",
  "playlists_page",
  "search_page",
  "smartlist_page",
  "collection_page",
];

const SMARTLIST_PAGE_DEFAULT_LABELS = {
  smartlist_page: {
    share: "Smartlistshare",
    mosaic: "smartlistsSettings",
  },
  collection_page: {
    share: "collectionShare",
    mosaic: "collectionsSettings",
  },
} as const;

export default function PlatformPageEdit(): JSX.Element | null {
  const { id: pageName, projectSlug } = useParams<{
    id: string;
    projectSlug: string;
    platform: Platform;
  }>();
  const { t } = useTranslation("editor-platform");
  const dispatch = useDispatch();

  const { WIDGET, CAROUSEL, MOSAIC, SHARE, SEARCH_PLACEHOLDER } =
    COMPONENT_TYPES;

  const token = useSelector((state: AppState) => state.loginReducer.token);
  const components = useSelector(
    (state: AppState) => state.fetchReducer.components,
  );
  const project = useSelector(
    (state: AppState) => state.architectureReducer.project,
  );
  const playlistsType = project?.storefront_project_params?.playlists_type;

  const [stateComponents, setStateComponents] = useState<PlatformComponent[]>(
    [],
  );
  const [stateProject, setStateProject] = useState(project);
  const [dataChanged, setDataChanged] = useState(false);

  const showPage = pageName === "signup_page" ? "/register" : null;

  const showHelper =
    pageName === "signup_page"
      ? {
          en: "https://support.okast.tv/en/support/solutions/articles/103000097745-customize-the-sign-up-fields-of-your-platform",
          fr: "https://support.okast.tv/fr/support/solutions/articles/103000097745-personnaliser-la-page-d-inscription-page-sign-up-de-vos-utilisateurs-%C3%A0-votre-plateforme",
        }
      : null;

  const itemHeader = {
    label: t(pageName),
  };

  const breadcrumbs = [
    {
      name: t("editor-menu:platform"),
      url: "/layout",
    },
    {
      name: t("title.layout"),
      url: "/layout",
    },
    {
      name: itemHeader.label,
    },
  ];

  const getSections = (): Section[] => {
    switch (pageName) {
      case "header_footer":
        return ["header", "footer"];
      case "my_playlists_page":
      case "my_list_page": {
        return ["playlists_page"];
      }
      default:
        return [pageName as Section];
    }
  };

  const initContentPageComponents = (
    componentList: PlatformComponent[],
  ): PlatformComponent[] => {
    const updatedComponentList = [...componentList];
    const shareComponent = getComponentByType(updatedComponentList, SHARE);
    const carouselComponent = getComponentByType(
      updatedComponentList,
      CAROUSEL,
    );
    const widgetComponent = getComponentByType(updatedComponentList, WIDGET);
    const carouselCollectionsComponent = getComponentByTypeOrName({
      components: updatedComponentList,
      type: CAROUSEL,
      name: "collections_contents",
    });

    if (!shareComponent) {
      updatedComponentList.push({
        section: "content_page",
        type: SHARE,
        rank: 1,
        label: t("contentShare"),
        enabled: true,
        params: {
          share_facebook: true,
          share_twitter: true,
          share_linkedin: true,
          share_mail: true,
          share_threads: true,
          display_custom_id: false,
        },
        dynamic_component: null,
        static_component: null,
        uuid: "new",
        platform: null,
      });
    }

    if (!carouselComponent) {
      updatedComponentList.push({
        section: "content_page",
        type: CAROUSEL,
        rank: 0,
        label: t("similarContents"),
        enabled: false,
        params: {
          arrows_on_hover: true,
          circular: true,
          component_per_slides: 6,
          display_arrows: true,
          display_item_title: false,
          display_title: true,
          display_dialog: true,
          ratio: "169",
        },
        static_component: {
          smartlist_module_name: "similar_contents",
        },
        dynamic_component: null,
        uuid: "new",
        platform: null,
      });
    }
    if (!carouselCollectionsComponent) {
      updatedComponentList.push({
        section: "content_page",
        type: CAROUSEL,
        rank: 0,
        label: t("collectionsLabel"),
        enabled: false,
        params: {
          arrows_on_hover: true,
          circular: true,
          component_per_slides: 6,
          display_arrows: false,
          display_item_title: false,
          display_title: true,
          display_dialog: true,
          ratio: "169",
        },
        static_component: {
          smartlist_module_name: "collections_contents",
        },
        dynamic_component: null,
        uuid: "new",
        platform: null,
      });
    }

    if (!widgetComponent) {
      updatedComponentList.push({
        section: "content_page",
        type: WIDGET,
        rank: 0,
        label: t("widget"),
        enabled: false,
        params: {
          width: "100%",
          height: "auto",
          enable_scroll: true,
          src_type: "html_template",
          external_url: "",
          template_url: "",
        },
        static_component: null,
        dynamic_component: null,
        uuid: "new",
        platform: null,
      });
    }
    return updatedComponentList;
  };

  const initSmartlistPageComponents = (
    componentList: PlatformComponent[],
    section: "smartlist_page" | "collection_page",
  ): PlatformComponent[] => {
    const updatedComponentList = [...componentList];
    const labels = SMARTLIST_PAGE_DEFAULT_LABELS[section];
    const shareComponent = getComponentByType(updatedComponentList, SHARE);
    const smartlistComponent =
      getComponentByType(updatedComponentList, MOSAIC) ??
      getComponentByType(updatedComponentList, CAROUSEL);
    const widgetComponent = getComponentByType(updatedComponentList, WIDGET);

    if (!shareComponent) {
      updatedComponentList.push({
        section,
        type: SHARE,
        rank: 1,
        label: t(labels.share),
        enabled: true,
        params: {
          share_facebook: false,
          share_twitter: false,
          share_linkedin: false,
          share_mail: false,
          share_threads: false,
          display_custom_id: false,
        },
        dynamic_component: null,
        static_component: null,
        uuid: "new",
        platform: null,
      });
    }
    if (!smartlistComponent) {
      updatedComponentList.push({
        section,
        type: MOSAIC,
        rank: 0,
        label: t(labels.mosaic),
        enabled: true,
        params: {
          display_dialog: true,
          display_item_title: true,
          display_title: true,
          individual_carousel: true,
          opts_individual_carousel: {
            component_per_slides: 8,
            display_button: false,
          },
          ratio: "169",
        },
        dynamic_component: null,
        static_component: null,
        uuid: "new",
        platform: null,
      });
    }

    if (!widgetComponent) {
      updatedComponentList.push({
        section,
        type: WIDGET,
        rank: 0,
        label: t("editor-widget:widget"),
        enabled: false,
        params: {
          width: "100%",
          height: "auto",
          enable_scroll: true,
          src_type: "html_template",
          external_url: "",
          template_url: "",
        },
        dynamic_component: null,
        static_component: null,
        uuid: "new",
        platform: null,
      });
    }

    return updatedComponentList;
  };

  const initCastingPageComponents = (
    componentList: PlatformComponent[],
  ): PlatformComponent[] => {
    const updatedComponentList = [...componentList];
    const widgetComponent = getComponentByType(updatedComponentList, WIDGET);

    if (!widgetComponent) {
      updatedComponentList.push({
        section: "casting_page",
        type: WIDGET,
        rank: 0,
        label: t("editor-widget:widget"),
        enabled: false,
        params: {
          width: "100%",
          height: "auto",
          enable_scroll: true,
          src_type: "html_template",
          external_url: "",
          template_url: "",
        },
        dynamic_component: null,
        static_component: null,
        uuid: "new",
        platform: null,
      });
    }

    return updatedComponentList;
  };

  const initPlaylistsPageComponents = (
    componentList: PlatformComponent[],
  ): PlatformComponent[] => {
    const updatedComponentList = [...componentList];
    const playlistsComponent =
      getComponentByType(updatedComponentList, MOSAIC) ??
      getComponentByType(updatedComponentList, CAROUSEL);

    const isMultiplePlaylistsProject = playlistsType === "multiple";

    if (!playlistsComponent) {
      updatedComponentList.push({
        section: "playlists_page",
        type: isMultiplePlaylistsProject ? CAROUSEL : MOSAIC,
        static_component: null,
        enabled: true,
        label: "Playlists setting page",
        rank: 0,
        params: {
          ratio: "169",
          display_title: true,
          display_item_title: false,
          display_dialog: true,
          individual_carousel: false,
          opts_individual_carousel: null,
        },
        dynamic_component: null,
        uuid: "new",
        platform: null,
      } as PlatformComponent);
    }

    return updatedComponentList;
  };

  const initSearchPageComponents = (
    componentList: PlatformComponent[],
  ): PlatformComponent[] => {
    const updatedComponentList = [...componentList];
    const defaultSearchComponent = updatedComponentList.find(
      (comp: PlatformComponent) => comp.rank === 1,
    );
    const placeholderInputSearchComponent = updatedComponentList.find(
      (comp: PlatformComponent) => comp.type === SEARCH_PLACEHOLDER,
    );

    if (!defaultSearchComponent) {
      updatedComponentList.push({
        section: "search_page",
        type: MOSAIC,
        rank: 1,
        label: t("defaultSearchPage"),
        enabled: true,
        params: {
          ratio: "169",
          display_title: true,
          display_item_title: false,
          display_dialog: true,
          individual_carousel: false,
          opts_individual_carousel: null,
        },
        dynamic_component: null,
        static_component: null,
        uuid: "new",
        platform: null,
      });
    }
    if (!placeholderInputSearchComponent) {
      updatedComponentList.push({
        section: "search_page",
        type: SEARCH_PLACEHOLDER,
        rank: 0,
        label: t("placeholder_search"),
        enabled: false,
        params: {
          placeholders: [],
        },
        dynamic_component: null,
        static_component: null,
        uuid: "new",
        platform: null,
      });
    }
    return updatedComponentList;
  };

  const getComponentsByPage = (
    componentList: PlatformComponent[],
  ): PlatformComponent[] => {
    switch (pageName) {
      case "content_page":
        return initContentPageComponents(componentList);
      case "smartlist_page":
      case "collection_page":
        return initSmartlistPageComponents(componentList, pageName);
      case "casting_page":
        return initCastingPageComponents(componentList);
      case "my_playlists_page":
      case "my_list_page":
        return initPlaylistsPageComponents(componentList);
      case "search_page":
        return initSearchPageComponents(componentList);
      default:
        return componentList;
    }
  };

  const filterComponents = (): void => {
    const sections = getSections();

    const newComponents = sections
      .flatMap((section) =>
        components?.indexes?.section?.[section]?.map(
          (index) => components.data[index],
        ),
      )
      .filter((component) => !!component);

    const finalComponents = getComponentsByPage(newComponents).sort(
      (a: PlatformComponent, b: PlatformComponent) => a.rank - b.rank,
    );
    setStateComponents(finalComponents);
  };

  // Keyed on `data`: request lifecycle actions recreate `components` with stale data, which would wipe unsaved edits.
  useEffect(() => {
    if (components) {
      filterComponents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [components?.data, pageName]);

  if (!stateProject) return null;

  const showToastSuccess = (successMessage: string): void => {
    dispatch(notifSuccess(successMessage));
  };

  const showToastError = (errorMessage: string, details?: string[]): void => {
    dispatch(notifError(errorMessage, details));
  };

  const setLocalProject = (updatedProject: Project): void => {
    dispatch(setProject({ project: updatedProject, projectSlug }));
  };

  const dispatchUpdateProject = async (
    onUpdateProject: () => void,
  ): Promise<void> =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    dispatch(
      putApi({
        endpoint: "/api/project/v3/admin/storefront_projects",
        headers: [
          {
            key: "xx-ok-project",
            value: projectSlug,
          },
        ],
        type: UPDATE_PROJECT,
        item: "project",
        token,
        data: stateProject,
        callback: onUpdateProject,
      }),
    );

  const dispatchSaveComponent = async (
    uuid: string,
    item: PlatformComponent,
  ): Promise<void> =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    dispatch(
      putApi({
        endpoint: `/api/project/v3/admin/components/${uuid}`,
        type: UPDATE_COMPONENT,
        item: "components",
        token,
        headers: [
          {
            key: "xx-ok-project",
            value: projectSlug,
          },
        ],
        data: item,
      }),
    );

  const createComponent = async ({
    component,
    callback,
  }: {
    component: PlatformComponent;
    callback?: () => void;
  }): Promise<void> =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    dispatch(
      postApi({
        endpoint: "/api/project/v3/admin/components/create",
        type: CREATE_COMPONENT,
        item: "components",
        token,
        headers: [
          {
            key: "xx-ok-project",
            value: projectSlug,
          },
        ],
        data: component,
        message: t("editor-notif:saveSuccess"),
        error: t("editor-notif:saveError"),
        callback,
      }),
    );

  const handleProjectParams = <T extends keyof StorefrontProjectParams>(
    key: T,
    value: StorefrontProjectParams[T],
  ): void => {
    setStateProject((prevState) => ({
      ...(prevState ?? stateProject),
      storefront_project_params: {
        ...(prevState ?? stateProject).storefront_project_params,
        [key]: value,
      },
    }));
    setDataChanged(true);
  };

  const handleChangeProject = (projectChanged: Project): void => {
    setStateProject({
      ...projectChanged,
      page_links: projectChanged.page_links.map((link: PageLink) => {
        const translations = projectChanged.translations.map(
          (translation: ProjectTranslation) => {
            const { title, url } = getPageLinkTranslation(link, translation);
            return {
              default: translation.default,
              language: translation.language,
              title,
              url,
              uuid: translation.uuid,
            };
          },
        );
        return {
          ...link,
          translations,
        };
      }),
    });
    setDataChanged(true);
  };

  const handleChangeComponents = (
    newComponents: PlatformComponent[],
    disableDataChange?: boolean,
  ): void => {
    setStateComponents([...newComponents]);
    setDataChanged(disableDataChange ? dataChanged : true);
  };

  const componentsErrors = (): string[] => {
    const errors: string[] = [];
    stateComponents.forEach((component) => {
      if (component.type === WIDGET && component.enabled) {
        if (
          component.params.src_type === WIDGET_URL_TYPE &&
          !component.params.external_url
        ) {
          errors.push(t("emptyUrlWidget"));
        }
        if (
          component.params.src_type === WIDGET_HTML_TYPE &&
          !component.params.template_temp &&
          !component.params.template_url
        ) {
          errors.push(t("emptyCodeWidget"));
        }
      }
    });
    return errors;
  };

  const handleUpdateProject = (): void => {
    ProjectAPI.getProject(projectSlug).then((updatedProject) => {
      setLocalProject(updatedProject);
      setStateProject(updatedProject);
    });
  };

  const handleSaveComponent = async (
    component: PlatformComponent,
    isNew: boolean,
  ): Promise<void> => {
    const updatedComponent = {
      ...component,
    };

    if (
      updatedComponent.type === "widget" &&
      updatedComponent.params.template_temp
    ) {
      delete updatedComponent.params.template_temp;
    }

    if (!isNew) {
      dispatchSaveComponent(updatedComponent.uuid, updatedComponent);
      return;
    }

    createComponent({
      component: updatedComponent,
      callback: () => {
        history.push(`${PROJECTS_ROUTE}/${projectSlug}/layout`);
      },
    });
  };

  const handleSaveTemplate = async (
    component: WidgetComponent,
    isNew: boolean,
  ): Promise<void> => {
    const blob = new Blob([component.params.template_temp ?? ""], {
      type: "text/html",
    });

    const formData = new FormData();
    if (component.uuid) {
      formData.append("storefront_project_component_uuid", component.uuid);
    }

    formData.append("file", blob);

    uploadWidget(
      formData,
      token,
      projectSlug,
      (response: {
        data: {
          url: string;
          uuid: string;
        };
      }) => {
        const updatedComponent = {
          ...component,
          uuid: component.uuid || response.data.uuid,
          params: {
            ...component.params,
            template_url: response.data.url,
          },
        };

        handleSaveComponent(updatedComponent, isNew);
      },
    );
  };

  const handleSave = (): void => {
    const errors = componentsErrors();

    if (errors.length > 0) {
      showToastError(t("editor-notif:saveError"), errors);
    } else {
      const dispatchList: Promise<void>[] = [];

      if (["signup_page", "header_footer", "offers_page"].includes(pageName)) {
        dispatchList.push(dispatchUpdateProject(handleUpdateProject));
      }

      if (pageName !== "signup_page") {
        const newComponents = stateComponents.map((component) => {
          const updatedComponent = {
            ...component,
          };

          if (
            (updatedComponent.last_rank || updatedComponent.last_rank === 0) &&
            updatedComponent.last_rank !== updatedComponent.rank
          ) {
            const item = {
              rank: updatedComponent.rank,
            };
            dispatchList.push(
              dispatchSaveComponent(
                updatedComponent.uuid,
                item as PlatformComponent,
              ),
            );
          } else if (
            SECTIONS_WITH_SAVED_COMPONENTS.includes(updatedComponent.section)
          ) {
            const isNew = updatedComponent.uuid === "new";
            if (
              updatedComponent.type === WIDGET &&
              updatedComponent.params.src_type === WIDGET_HTML_TYPE &&
              updatedComponent.params.template_temp
            ) {
              dispatchList.push(handleSaveTemplate(updatedComponent, isNew));
            } else {
              dispatchList.push(handleSaveComponent(updatedComponent, isNew));
            }
          }

          updatedComponent.last_rank = null;

          return updatedComponent;
        });

        setStateComponents(newComponents);
      }

      Promise.all(dispatchList)
        .then(() => {
          showToastSuccess(t("editor-notif:saveSuccess"));
        })
        .catch(() => {
          showToastError(t("editor-notif:saveError"));
        });
      setDataChanged(false);
    }
  };

  const getComponentsEditor = (): JSX.Element | null => {
    switch (pageName) {
      case "smartlist_page":
        return (
          <SmartlistPageEditor
            components={stateComponents}
            onChangeComponents={handleChangeComponents}
            section="smartlist_page"
          />
        );
      case "collection_page":
        return (
          <SmartlistPageEditor
            components={stateComponents}
            onChangeComponents={handleChangeComponents}
            section="collection_page"
          />
        );
      case "casting_page":
        return (
          <CastingPageEditor
            components={stateComponents}
            onChangeComponents={handleChangeComponents}
          />
        );
      case "my_playlists_page":
      case "my_list_page":
        return (
          <CommonTypeEditor
            components={stateComponents}
            onChangeComponents={handleChangeComponents}
            pageType="playlists"
            imageFormatList={[
              ...DEFAULT_IMAGE_FORMATS,
              ...ADDITIONNAL_IMAGE_FORMATS,
            ]}
          />
        );
      case "content_page":
        return (
          <ContentPageEditor
            components={stateComponents as ContentPagePlatformComponent[]}
            onChangeComponents={handleChangeComponents}
          />
        );
      case "search_page":
        return (
          <SearchPageEditor
            components={stateComponents as SearchPagePlatformComponent[]}
            onChangeComponents={handleChangeComponents}
          />
        );
      case "signup_page":
        return (
          <SignupPageEditor
            changeProject={handleChangeProject}
            changeProjectParams={handleProjectParams}
            project={stateProject}
          />
        );
      case "offers_page":
        return <OffersPageEditor project={stateProject} />;
      default:
        history.replace(`${PROJECTS_ROUTE}/${projectSlug}/layout`);
        return null;
    }
  };

  return (
    <div
      className="editor-container editor-platform editor-header-simple editor-has-breadcrumbs"
      data-testid="PlatformPageEdit"
    >
      <Prompt message={t("unsaved")} when={dataChanged} />
      <Breadcrumbs items={breadcrumbs} />
      <ContainerHeader
        withBackBtn
        infoLinks={showHelper}
        item={itemHeader}
        onSave={dataChanged ? handleSave : null}
        showPage={
          stateProject?.enabled && showPage
            ? stateProject.base_url + showPage
            : null
        }
      />
      {getComponentsEditor()}
    </div>
  );
}
